/**
 * Jedno źródło formatu daty z godziną w interfejsie — bez sekund, w czasie
 * polskim.
 *
 * `toLocaleString('pl-PL')` bez opcji daje „28.09.2026, 10:34:10": w wąskiej
 * kolumnie łamie się na dwie-trzy linie, a sekundy przy szacunku dolotu
 * (±8%) udają precyzję, której nie ma. Dwa zapisy:
 * - szczegóły, pasek synchronizacji, historia, dzwonek: „28.09.2026, 10:34"
 *   (`dateTimeNoSeconds`, a w kolumnach list `DateTimeField` z `DateFields`);
 * - komórki tabel, gdzie rok nic nie wnosi: „28.09, 10:34" (`shortDateTime`).
 * Pełny zapis z sekundami idzie do dymka (`fullDateTime`) — tam, gdzie
 * sekunda bywa dowodem (dziennik działań), informacja zostaje.
 *
 * ## Strefa: zawsze Warszawa (28.09.2026)
 *
 * API oddaje chwile w prawdziwym UTC (`…Z`). Pokazujemy je **jawnie w czasie
 * polskim**, niezależnie od strefy przeglądarki — spedytor w Niemczech albo
 * na wyjeździe ma widzieć tę samą godzinę co w TMS. Wcześniej format szedł
 * w strefie przeglądarki.
 *
 * ## Daty bez godziny to daty
 *
 * Termin ważności, ważności uprawnień, nieobecności, pierwsza rejestracja
 * leżą w bazie jako północ UTC dnia (klasa „data”, API
 * `shared/client-time.ts`). Pokazujemy je `calendarDate` — dzień z zapisu,
 * w UTC, bez godziny. Przez strefę przeglądarki „05.10” wychodziło jako
 * „05.10.2026, 02:00”, a w Nowym Jorku jako 04.10.
 *
 * Przeniesione z `admin/userOffers/shortDate.ts` (28.09.2026), żeby
 * `fmtDate` z `Panel` i klocki list nie miały każdy własnego formatu.
 *
 * ## Zawsze z zerem (30.09.2026)
 *
 * Dzień i miesiąc zawsze dwucyfrowe: „01.09.2026”, nigdy „1.09.2026”.
 * Wszystkie zapisy składamy z `warsawParts`, nie z `toLocaleString('pl-PL')`
 * — ten bez jawnych opcji nie dopełnia zerem, a `DateField` react-admina
 * z opcjami zależał od ICU przeglądarki. Dlatego listy mają własne pola
 * (`shared/DateFields.tsx`) zamiast `DateField`.
 */

export const CLIENT_TIME_ZONE = 'Europe/Warsaw';

const pad = (n: number) => String(n).padStart(2, '0');

const parse = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: CLIENT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Czas polski w danej chwili — liczby, bez formatowania. */
export function warsawParts(d: Date): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const p: Record<string, number> = {};
  for (const part of partsFormatter.formatToParts(d)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  return {
    year: p.year,
    month: p.month,
    day: p.day,
    hour: p.hour === 24 ? 0 : p.hour,
    minute: p.minute,
    second: p.second,
  };
}

/**
 * „13.10” — dzień w Polsce, w którym wypada CHWILA, bez roku i godziny; do
 * komórek tabel, gdy chwila jest początkiem dnia (koniec nieobecności).
 * Brak = „—”.
 */
export function shortDay(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}`;
}

/** „28.09, 10:34" — do komórek tabel; brak daty = „—". */
export function shortDateTime(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}, ${pad(w.hour)}:${pad(w.minute)}`;
}

/** „28.09.2026, 10:34" — do wierszy szczegółów, gdzie rok ma znaczenie. */
export function dateTimeNoSeconds(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}.${w.year}, ${pad(w.hour)}:${pad(w.minute)}`;
}

/**
 * „01.10.2026, 05:08:26” — pełny zapis z sekundami, do dymka nad skróconą
 * datą. Składany ręcznie jak pozostałe: `toLocaleString('pl-PL')` nie
 * dopełnia dnia i miesiąca zerem („1.10.2026”), a dymek stał obok komórki
 * z „01.10” (30.09.2026).
 */
export function fullDateTime(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}.${w.year}, ${pad(w.hour)}:${pad(w.minute)}:${pad(w.second)}`;
}

/** „08:00” — sama godzina chwili w czasie polskim; brak = „—”. */
export function clientTime(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.hour)}:${pad(w.minute)}`;
}

/**
 * Przedział dwóch chwil: w obrębie jednego dnia bez powtórzonej daty
 * („29.09.2026, 08:00–15:00”), przez północ z dwiema pełnymi datami, a gdy
 * jest tylko jeden kraniec albo oba równe — sama chwila. Nic = „—”.
 */
export function dateTimeRange(from?: string | null, to?: string | null): string {
  const start = parse(from);
  const end = parse(to);
  if (!start && !end) return '—';
  if (!start || !end || start.getTime() === end.getTime()) {
    return dateTimeNoSeconds(from && start ? from : to);
  }
  if (clientDayKey(start) === clientDayKey(end)) return `${dateTimeNoSeconds(from)}–${clientTime(to)}`;
  return `${dateTimeNoSeconds(from)} – ${dateTimeNoSeconds(to)}`;
}

/** „28.09.2026” — dzień w Polsce, w którym wypada CHWILA; brak = „—”. */
export function clientDay(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}.${w.year}`;
}

/**
 * „05.10.2026” — DATA bez godziny (północ UTC dnia w bazie): dzień z zapisu,
 * bez przeliczania strefy. Brak = „—”.
 */
export function calendarDate(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '—';
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

/** Dzień polski danej chwili jako `YYYY-MM-DD` — do porównań z datami. */
export function clientDayKey(d: Date): string {
  const w = warsawParts(d);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/**
 * Czy termin-DATA (`do 05.10`) już minął: od początku tego dnia w Polsce.
 * Ta sama reguła co w API (`calendarDatePassed`, `shared/client-time.ts`).
 */
export function calendarDatePassed(iso: string | null | undefined, now: Date = new Date()): boolean {
  const d = parse(iso);
  if (!d) return false;
  return d.toISOString().slice(0, 10) <= clientDayKey(now);
}

/**
 * Czy termin ważności uprawnienia (`ważne do 12.09`) już się skończył:
 * „do 12.09” obejmuje cały 12.09 w Polsce, wygasa z początkiem 13.09.
 * Jedna reguła dla uprawnień pojazdu i kierowcy (decyzja użytkownika
 * 08.10.2026) — ta sama co w API (`validityEnded`, `shared/client-time.ts`).
 * Brak albo nieczytelna data = nie wygasło (brak terminu to nie termin).
 */
export function validityEnded(iso: string | null | undefined, now: Date = new Date()): boolean {
  const d = parse(iso);
  if (!d) return false;
  return d.toISOString().slice(0, 10) < clientDayKey(now);
}
